import { Formik } from "formik";
import { FC, useContext, useState } from "react";
import DatePicker from "react-date-picker";
import Select, { components } from "react-select";
import * as yup from "yup";

import appStyles from "../../../App.module.css";
import appConstants, { addDays, totalDaysInCurrentYear } from "../../../app-constants";
import { AUBusinessTravelContext } from "../../AUBusinessTravel";
import {
  btCurrencyProductIds,
  colourStyles,
  currencyDropDown,
  getProduct,
  noOfEmployee,
} from "../../config/bt-constants";
import { IAUBusinessTravelContext, IQuoteFrom, IinitialQuote } from "../../config/interfaces";
import PopoverIcon from "../PopoverIcon";
import Popup from "../Popup";
import Footer from "../common/Footer";
import SearchIcon from "../icons/SearchIcon";
import CoveredMoreThan250TripsModal from "../modal/CoveredMoreThan250TripsModal";
import LoadingModal from "../modal/LoadingModal";

interface QuoteFormProps {}

const QuoteForm: FC<QuoteFormProps> = (props: QuoteFormProps) => {
  const { next, quoteData, setQuoteData, setLoader, version, setQuoteResult, tripsData, loader, createQuote } =
    useContext(AUBusinessTravelContext) as IAUBusinessTravelContext;

  const [isMoreThan250, setIsMoreThan250] = useState(false);
  const [hasError, setHasError] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const initialFormValues: IQuoteFrom = {
    host_country: tripsData?.hostCountries?.find((country: any) => country.code === "AU"),
    currency: { value: "AUD", label: "AUD" },
    start_date: quoteData.start_date,
    end_date: quoteData.end_date,
    noOfTrips: quoteData.noOfTrips || "",
    noOfEmployee: quoteData.noOfEmployee,
  };

  const validationSchema = yup.object().shape({
    host_country: yup.object().required(),
    currency: yup.object().required(),
    start_date: yup.date().required(),
    end_date: yup.date().required(),
    noOfTrips: yup
      .number()
      .typeError("Please enter a valid number")
      .min(1, "Number of trips must be at least 1")
      .max(250, "Number of trips should not be more than 250")
      .required("This field is required"),
    noOfEmployee: yup.string().required(),
  });

  const handleFormSubmit = async (values: IQuoteFrom) => {
    try {
      setLoader("quote");
      let _quoteData = { ...quoteData };

      const currencyValue = values.currency?.value || "AUD";

      _quoteData.host_country = values.host_country;
      _quoteData.currency = currencyValue;
      _quoteData.productId = getProduct(currencyValue);
      _quoteData.start_date = values.start_date;
      _quoteData.end_date = values.end_date;
      _quoteData.noOfTrips = values.noOfTrips;
      _quoteData.noOfEmployee = values.noOfEmployee;

      setQuoteData(_quoteData);

      const createQuoteResponse = await createQuote(_quoteData);
      setQuoteResult(createQuoteResponse);
      next();
    } catch (e: any) {
      setHasError(e.message);
    } finally {
      setLoader(null);
    }
  };

  const style = { paddingLeft: "12px", lineHeight: 0 };
  const Control = ({ children, ...props }: any) => (
    <components.Control {...props}>
      <span style={style}>
        <SearchIcon />
      </span>
      {children}
    </components.Control>
  );

  return (
    <>
      {loader && (
        <Popup onClose={() => {}} title="" children={<LoadingModal />} closeButton={false} LoadingModal={true} />
      )}
      <div className={`${appStyles.mainContainer} ${appStyles.btW576}`}>
        <Formik initialValues={initialFormValues} validationSchema={validationSchema} onSubmit={handleFormSubmit}>
          {({ values, errors, touched, handleBlur, setFieldValue, handleSubmit, isValid, validateForm, dirty }) => (
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                await validateForm();
                handleSubmit();
              }}
            >
              <div className={` ${appStyles.formBox} `}>
                <div className={appStyles.formWrapper}>
                  <div className={appStyles.formRow}>
                    <div className={appStyles.formBlock}>
                      <div
                        className={`${appStyles.formField} ${
                          touched?.start_date && errors?.start_date ? appStyles.hasError : ""
                        }`}
                      >
                        <label className={appStyles.btFormLabel}>Policy start date</label>
                        <DatePicker
                          calendarClassName={`${appStyles.bfpDatePickerCalendar} ${appStyles.btDatePickerCalendar}`}
                          className={`${appStyles.bfpDatePicker} ${appStyles.btDatePicker} ${appStyles.bfpDatePickerCalendarStartDate} ${appStyles.btDatePickerCalendarStartDate}`}
                          dayPlaceholder="dd"
                          monthPlaceholder="mm"
                          yearPlaceholder="yyyy"
                          format={appConstants.datePickerFormat}
                          value={values.start_date}
                          maxDate={addDays(new Date(), 55)}
                          id="start_date"
                          isOpen={isOpen}
                          onClick={() => setIsOpen(true)}
                          onCalendarClose={() => setIsOpen(false)}
                          onChange={(value: any) => {
                            setFieldValue("start_date", value);

                            setFieldValue("end_date", addDays(value, totalDaysInCurrentYear(value)));
                          }}
                          onBlur={handleBlur("start_date")}
                          minDate={new Date()}
                        />
                        {touched?.start_date && errors?.start_date && (
                          <div className={appStyles.formError}>This field is required</div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className={appStyles.formRow}>
                    <div className={appStyles.formBlock}>
                      <label className={appStyles.btFormLabel}>Number of International trips</label>

                      <div
                        className={`${appStyles.formField} ${touched?.noOfTrips && errors?.noOfTrips ? appStyles.hasError : ""
                          }`}
                      >
                        <input
                          type="number"
                          name="noOfTrips"
                          id="noOfTrips"
                          // Applying the specific battleface classes for standard text inputs
                          className={`${appStyles.btInput} ${appStyles.bfpInput}`}
                          placeholder="Enter number of trips (1 – 250)"
                          // Type casting to bypass the TypeScript object error temporarily
                          value={(values.noOfTrips as unknown as string) || ""}
                          onBlur={handleBlur("noOfTrips")}
                          onChange={(e) => {
                            const val = e.target.value === "" ? "" : Number(e.target.value);
                            setFieldValue("noOfTrips", val);
                          }}
                          // Optional: match height/padding if your CSS uses these
                          style={{ width: '100%', height: '48px' }}
                        />

                        {touched?.noOfTrips && errors?.noOfTrips && (
                          <div className={appStyles.formError}>{errors.noOfTrips}</div>
                        )}
                      </div>

                      <p
                        className={errors?.noOfTrips ? appStyles.btFormError : appStyles.btNoteWrap}
                        style={{ marginTop: '20px' }}
                      >
                        This number should reflect international trips only. Domestic trips are unlimited. Each traveller counts as one trip (e.g., 3 employees on one international trip = 3 trips).
                      </p>
                    </div>
                  </div>

                  <div className={appStyles.formRow}>
                    <div className={appStyles.formBlock}>
                      <label className={appStyles.btFormLabel}>
                        Number of employees at company
                        <PopoverIcon text={"This product is only suitable for companies with 2 or more employees."} />
                      </label>

                      <div
                        className={`${appStyles.formField} ${appStyles.btFormField} ${
                          touched?.noOfEmployee && errors?.noOfEmployee ? appStyles.hasError : ""
                        }`}
                      >
                        {noOfEmployee.map((item, index) => (
                          <div key={index} className={`${appStyles.radioButton} ${appStyles.radioButtonSmall}`}>
                            <input
                              type="radio"
                              id={item.value}
                              name="noOfEmployee"
                              value={item.value}
                              onChange={(e) => {
                                setFieldValue("noOfEmployee", e.target.value);
                              }}
                            />
                            <label className="btn btn-default" htmlFor={item.value}>
                              {item.value}
                            </label>
                          </div>
                        ))}
                        {touched?.noOfEmployee && errors?.noOfEmployee && (
                          <div className={appStyles.formError}>This field is required</div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className={appStyles.formRow}>
                <div className={`${appStyles.buttonBlock}`}>
                  <button
                    className={`${appStyles.bfButton}  ${appStyles.btButton100}`}
                    type="submit"
                    disabled={!isValid || !dirty}
                  >
                    SEE YOUR QUOTE
                  </button>
                </div>
              </div>
            </form>
          )}
        </Formik>

        {hasError && (
          <div className={appStyles.formRow}>
            <div className={appStyles.battlefaceServerError}>{hasError}</div>
          </div>
        )}
        

        <Footer />
      </div>
    </>
  );
};

export default QuoteForm;
